// File: src/user/PrivateRoute.js
//
// Route guard for the /profile/* tree (and any other future protected
// route). Rules in priority order:
//
//   1. Never redirect while Firebase Auth is still resolving the
//      persisted state on this tab. Doing that on first paint is what
//      caused users to see /profile flash to /login and then bounce
//      back to /profile after Firebase fired onAuthStateChanged. We
//      now render a small spinner via <RouteFallback /> until
//      `authLoading` is false, exactly matching the lazy-route
//      loading shell so the user just sees a single uninterrupted
//      "page is loading" state.
//
//   2. SMS auto-login in flight (`smsAuthState === 'pending'`) — show
//      a "signing you in from your SMS link…" spinner. Same component
//      as (1) but with a tailored label so a stuck spinner is
//      diagnosable from a screenshot.
//
//   3. SMS auto-login failed (`smsAuthState === 'failed'` and no
//      `user`) — render an inline retry surface. We deliberately do
//      NOT Navigate to /login here. The destination URL (e.g.
//      /profile/account?lexai=phone) is intentionally meaningful, and
//      the old behavior of bouncing to /login lost it the moment the
//      auth context emitted `authLoading=false, user=null`. The user
//      now sees a clear "couldn't sign you in from your SMS link"
//      message with a Sign In button that preserves the current path
//      via /login?next=.
//
//   4. Generic signed-out → /login (with `from=...` so the post-login
//      redirect lands exactly where they tried to go).
//
//   5. Signed-in but with an unverified email/password account →
//      /login?verify=1&email=...&next=... so we can show the resend-
//      verification UI.
//
//   6. Signed-in & verified → render children.
import React, { useContext } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import RouteFallback from '../components/RouteFallback';

function currentPathWithSearchAndHash(location) {
  const path = location.pathname || '/';
  const search = location.search || '';
  const hash = location.hash || '';
  return `${path}${search}${hash}`;
}

function SmsAuthFailureSurface({ location }) {
  const nextHref = `/login?next=${encodeURIComponent(currentPathWithSearchAndHash(location))}`;
  // Inline styles only — PrivateRoute is shared infra and we don't want
  // to introduce a new CSS file just for this rare failure path. The
  // surface is intentionally low-key (matches the RouteFallback shell)
  // so it doesn't feel like an error screen.
  return (
    <div
      role="alert"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        gap: '12px',
        textAlign: 'center',
        color: '#222',
      }}
    >
      <div style={{ fontSize: '20px', fontWeight: 600 }}>
        Couldn't sign you in from your SMS link
      </div>
      <div style={{ fontSize: '14px', color: '#555', maxWidth: '360px' }}>
        The link may have expired or already been used. Sign in once and
        we'll send you straight back to this page.
      </div>
      <Link
        to={nextHref}
        style={{
          marginTop: '8px',
          padding: '10px 18px',
          borderRadius: '999px',
          background: '#111',
          color: '#fff',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '14px',
        }}
      >
        Sign in
      </Link>
    </div>
  );
}

const PrivateRoute = ({ children }) => {
  const { user, authLoading, smsAuthState } = useContext(AuthContext);
  const location = useLocation();

  // Auth still resolving — show the loading shell instead of a
  // misleading redirect. This is the single change that eliminates
  // the "refresh on /profile → kicked to /login" bug for users with
  // a valid persisted session.
  if (authLoading) {
    return <RouteFallback minHeight="60vh" label="Loading your account…" />;
  }

  // 2026-05-19 — SMS exchange in flight. authLoading already flipped to
  // false (the onAuthStateChanged listener resolved, just with the
  // pre-exchange anonymous state), but the customToken sign-in promise
  // hasn't returned yet. Show a tailored spinner so we don't appear
  // stuck OR bounce to /login prematurely.
  if (smsAuthState === 'pending' && !user) {
    return <RouteFallback minHeight="60vh" label="Signing you in from your SMS link…" />;
  }

  // 2026-05-19 — SMS exchange returned an error (expired / revoked /
  // bad signature). Don't hard-bounce to /login because that strips
  // the destination path the SMS promised the user. Show an inline
  // retry surface with a Sign-In button that preserves the URL.
  if (smsAuthState === 'failed' && !user) {
    return <SmsAuthFailureSurface location={location} />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const providerIds = Array.isArray(user.providerIds) ? user.providerIds : [];
  const needsVerification = providerIds.includes('password') && !user.emailVerified;
  if (needsVerification) {
    const qs = new URLSearchParams();
    qs.set('verify', '1');
    if (user.email) qs.set('email', user.email);
    qs.set('next', `${location.pathname || '/app'}${location.search || ''}${location.hash || ''}`);
    return <Navigate to={`/login?${qs.toString()}`} state={{ from: location }} replace />;
  }

  return children;
};

export default PrivateRoute;
